import { addDoc, collection } from 'firebase/firestore';
import { db } from './firebase';
import { saveRecurringGameInstances } from './gameSync';

// 🎯 SAMPLE DATA GENERATOR FOR TESTING
// This utility helps create sample data for PlayerHub testing

export const createSampleTeams = async () => {
  const sampleTeams = [
    {
      name: 'Trivia Titans',
      leagueId: 'ATL001',
      homeVenue: '57th Fighter Group Restaurant',
      gpa: 3.85,
      description: 'Atlanta\'s premier trivia team',
      captain: 'sample-captain-1',
      members: ['sample-captain-1', 'member-1', 'member-2'],
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      name: 'Quiz Masters',
      leagueId: 'ATL002',
      homeVenue: 'Local Bar & Grill',
      gpa: 3.72,
      description: 'Consistent performers with deep knowledge',
      captain: 'sample-captain-2',
      members: ['sample-captain-2', 'member-3', 'member-4'],
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      name: 'Brainiacs',
      leagueId: 'ATL003',
      homeVenue: 'Downtown Pub',
      gpa: 3.68,
      description: 'Science and history specialists',
      captain: 'sample-captain-3',
      members: ['sample-captain-3', 'member-5', 'member-6'],
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      name: 'Wise Guys',
      leagueId: 'ATL004',
      homeVenue: 'Sports Bar Central',
      gpa: 3.55,
      description: 'Sports and pop culture experts',
      captain: 'sample-captain-4',
      members: ['sample-captain-4', 'member-7', 'member-8'],
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      name: 'Smart Cookies',
      leagueId: 'ATL005',
      homeVenue: 'Cafe Corner',
      gpa: 3.42,
      description: 'Food and travel trivia champions',
      captain: 'sample-captain-5',
      members: ['sample-captain-5', 'member-9', 'member-10'],
      createdAt: new Date(),
      updatedAt: new Date()
    }
  ];

  const createdTeams = [];
  for (const team of sampleTeams) {
    try {
      const docRef = await addDoc(collection(db, 'teams'), team);
      createdTeams.push({ id: docRef.id, ...team });
      console.log(`Created sample team: ${team.name}`);
    } catch (error) {
      console.error(`Failed to create team ${team.name}:`, error);
    }
  }

  return createdTeams;
};

export const createSampleGameResults = async () => {
  const sampleResults = [
    {
      gameData: {
        title: 'Tuesday Trivia Night',
        venue: { name: '57th Fighter Group Restaurant' }
      },
      venue: { name: '57th Fighter Group Restaurant' },
      teams: [
        { name: 'Trivia Titans', score: 85 },
        { name: 'Quiz Masters', score: 78 },
        { name: 'Brainiacs', score: 72 }
      ],
      completedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), // 2 days ago
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      gameData: {
        title: 'Thursday Trivia Challenge',
        venue: { name: 'Local Bar & Grill' }
      },
      venue: { name: 'Local Bar & Grill' },
      teams: [
        { name: 'Wise Guys', score: 92 },
        { name: 'Smart Cookies', score: 88 },
        { name: 'Trivia Titans', score: 85 }
      ],
      completedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000), // 5 days ago
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      gameData: {
        title: 'Weekend Warriors',
        venue: { name: 'Downtown Pub' }
      },
      venue: { name: 'Downtown Pub' },
      teams: [
        { name: 'Brainiacs', score: 89 },
        { name: 'Quiz Masters', score: 86 },
        { name: 'Wise Guys', score: 83 }
      ],
      completedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 1 week ago
      createdAt: new Date(),
      updatedAt: new Date()
    }
  ];

  const createdResults = [];
  for (const result of sampleResults) {
    try {
      const docRef = await addDoc(collection(db, 'gameResults'), result);
      createdResults.push({ id: docRef.id, ...result });
      console.log(`Created sample game result: ${result.gameData.title}`);
    } catch (error) {
      console.error(`Failed to create game result ${result.gameData.title}:`, error);
    }
  }

  return createdResults;
};

export const createSampleRecurringGames = async () => {
  const sampleRecurringGames = [
    {
      gameTitle: 'Tuesday Trivia Night',
      gameDescription: 'Join us every Tuesday for an exciting evening of trivia!',
      venueName: '57th Fighter Group Restaurant',
      venueAddress: '3829 Clairmont Rd, Atlanta, GA 30341',
      venueLocation: 'Atlanta, GA',
      hostName: 'Trivia Plug Pat',
      hostId: 'sample-host-1',
      isActive: true,
      isRecurring: true,
      recurringDays: {
        monday: false,
        tuesday: true,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
        sunday: false
      },
      recurringTime: '19:00',
      status: 'scheduled',
      scheduledDateTime: new Date(),
      recurringStartDate: new Date(),
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      gameTitle: 'Thursday Trivia Challenge',
      gameDescription: 'Test your knowledge every Thursday night!',
      venueName: 'Local Bar & Grill',
      venueAddress: '123 Main St, Atlanta, GA 30301',
      venueLocation: 'Atlanta, GA',
      hostName: 'Quiz Master',
      hostId: 'sample-host-2',
      isActive: true,
      isRecurring: true,
      recurringDays: {
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: true,
        friday: false,
        saturday: false,
        sunday: false
      },
      recurringTime: '20:00',
      status: 'scheduled',
      scheduledDateTime: new Date(),
      recurringStartDate: new Date(),
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      gameTitle: 'Weekend Warriors',
      gameDescription: 'Saturday afternoon trivia for the whole family!',
      venueName: 'Downtown Pub',
      venueAddress: '456 Oak Ave, Atlanta, GA 30302',
      venueLocation: 'Atlanta, GA',
      hostName: 'Weekend Host',
      hostId: 'sample-host-3',
      isActive: true,
      isRecurring: true,
      recurringDays: {
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: true,
        sunday: false
      },
      recurringTime: '15:00',
      status: 'scheduled',
      scheduledDateTime: new Date(),
      recurringStartDate: new Date(),
      createdAt: new Date(),
      updatedAt: new Date()
    }
  ];

  const createdGames = [];
  for (const game of sampleRecurringGames) {
    try {
      // Save parent recurring game
      const parentDocRef = await addDoc(collection(db, 'scheduledGames'), game);
      const parentGameWithId = { ...game, id: parentDocRef.id };
      
      // Generate instances
      const instances = await saveRecurringGameInstances(parentGameWithId, 8);
      
      createdGames.push({
        parent: { id: parentDocRef.id, ...game },
        instances: instances
      });
      
      console.log(`Created sample recurring game: ${game.gameTitle} with ${instances.length} instances`);
    } catch (error) {
      console.error(`Failed to create recurring game ${game.gameTitle}:`, error);
    }
  }

  return createdGames;
};

export const createSampleVenues = async () => {
  const sampleVenues = [
    {
      name: '57th Fighter Group Restaurant',
      address: '3829 Clairmont Rd, Atlanta, GA 30341',
      city: 'Atlanta',
      state: 'GA',
      zipCode: '30341',
      country: 'US',
      phone: '(404) 636-0780',
      website: 'https://57thfightergroup.com',
      description: 'Historic aviation-themed restaurant with great trivia atmosphere',
      capacity: 150,
      triviaNights: ['Tuesday', 'Thursday'],
      contactPerson: 'John Smith',
      contactEmail: 'john@57thfightergroup.com',
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      name: 'Local Bar & Grill',
      address: '123 Main St, Atlanta, GA 30301',
      city: 'Atlanta',
      state: 'GA',
      zipCode: '30301',
      country: 'US',
      phone: '(404) 555-0123',
      website: 'https://localbaratl.com',
      description: 'Cozy neighborhood bar perfect for trivia nights',
      capacity: 80,
      triviaNights: ['Wednesday', 'Friday'],
      contactPerson: 'Sarah Johnson',
      contactEmail: 'sarah@localbaratl.com',
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      name: 'Downtown Pub',
      address: '456 Oak Ave, Atlanta, GA 30302',
      city: 'Atlanta',
      state: 'GA',
      zipCode: '30302',
      country: 'US',
      phone: '(404) 555-0456',
      website: 'https://downtownpubatl.com',
      description: 'Popular downtown pub with weekend trivia events',
      capacity: 120,
      triviaNights: ['Saturday'],
      contactPerson: 'Mike Wilson',
      contactEmail: 'mike@downtownpubatl.com',
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      name: 'Sports Bar Central',
      address: '789 Pine St, Atlanta, GA 30303',
      city: 'Atlanta',
      state: 'GA',
      zipCode: '30303',
      country: 'US',
      phone: '(404) 555-0789',
      website: 'https://sportsbarcentral.com',
      description: 'Sports-focused bar with multiple trivia nights',
      capacity: 200,
      triviaNights: ['Monday', 'Tuesday', 'Thursday'],
      contactPerson: 'Lisa Davis',
      contactEmail: 'lisa@sportsbarcentral.com',
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      name: 'Cafe Corner',
      address: '321 Elm St, Atlanta, GA 30304',
      city: 'Atlanta',
      state: 'GA',
      zipCode: '30304',
      country: 'US',
      phone: '(404) 555-0321',
      website: 'https://cafecorneratl.com',
      description: 'Charming cafe with afternoon trivia events',
      capacity: 60,
      triviaNights: ['Sunday'],
      contactPerson: 'David Brown',
      contactEmail: 'david@cafecorneratl.com',
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date()
    }
  ];

  const createdVenues = [];
  for (const venue of sampleVenues) {
    try {
      const docRef = await addDoc(collection(db, 'venues'), venue);
      createdVenues.push({ id: docRef.id, ...venue });
      console.log(`Created sample venue: ${venue.name}`);
    } catch (error) {
      console.error(`Failed to create venue ${venue.name}:`, error);
    }
  }

  return createdVenues;
};

export const generateAllSampleData = async () => {
  console.log('🎯 Generating sample data for testing...');
  
  try {
    // Create sample teams
    console.log('📊 Creating sample teams...');
    const teams = await createSampleTeams();
    
    // Create sample game results
    console.log('🏆 Creating sample game results...');
    const results = await createSampleGameResults();
    
    // Create sample recurring games
    console.log('🎮 Creating sample recurring games...');
    const games = await createSampleRecurringGames();
    
    // Create sample venues
    console.log('🏢 Creating sample venues...');
    const venues = await createSampleVenues();
    
    console.log('✅ Sample data generation complete!');
    console.log(`📊 Created ${teams.length} teams`);
    console.log(`🏆 Created ${results.length} game results`);
    console.log(`🎮 Created ${games.length} recurring games`);
    console.log(`🏢 Created ${venues.length} venues`);
    
    return {
      teams,
      results,
      games,
      venues
    };
  } catch (error) {
    console.error('❌ Error generating sample data:', error);
    throw error;
  }
}; 